// Swish App2App in the browser (Getswish "Swish app-to-app integration guide" v1.6).
//
//   swish://payment?data=<URL-encoded JSON>&callbackurl=<URL>&callbackresultparameter=<name>
//
// The JSON payload is built by the Klargo backend (payee = the seller-confirmed number, amount =
// the signed price, message = the deal reference, all editable: false) and is passed through
// here unchanged; this file only wraps it into the URL and reads the return.
//
// The callback result is NOT proof of payment (guide §3.2, §4.2, §6.1.2: "there are ways to
// manipulate this callback"). It is only ever used to choose which explanation to show; it never
// calls the backend and never changes any payment state.

export const SWISH_RETURN_PARAM = "swish";
export const SWISH_RETURN_VALUE = "return";
export const SWISH_RESULT_PARAM = "swishResult";
// Which instruction was opened in Swish. The return can land in another tab or browser, so the
// page cannot rely on its own memory; and the seller may have changed the number meanwhile.
export const SWISH_FOR_PARAM = "swishFor";

const FINGERPRINT = /^[A-Za-z0-9_-]{1,128}$/;

/** The page the Swish app opens when the payment is finished: this deal, marked as a return. */
export function swishReturnUrl(contractId, { fingerprint, origin = window.location.origin } = {}) {
  let url = `${origin}/Contract/${encodeURIComponent(contractId)}?${SWISH_RETURN_PARAM}=${SWISH_RETURN_VALUE}`;
  if (fingerprint && FINGERPRINT.test(fingerprint)) url += `&${SWISH_FOR_PARAM}=${fingerprint}`;
  return url;
}

/** swish://payment?data=…&callbackurl=…&callbackresultparameter=… (guide §3.2.3, §6). */
export function buildSwishAppUrl(payloadJson, callbackUrl) {
  if (typeof payloadJson !== "string" || !payloadJson.startsWith("{")) {
    throw new Error("A Swish payload from the server is required.");
  }
  let url = `swish://payment?data=${encodeURIComponent(payloadJson)}`;
  if (callbackUrl) {
    url += `&callbackurl=${encodeURIComponent(callbackUrl)}&callbackresultparameter=${SWISH_RESULT_PARAM}`;
  }
  return url;
}

const RESULTS = ["paid", "notpaid", "unknown"];

/**
 * Reads a return from Swish off the page URL.
 *   null                     — the page was not opened by Swish
 *   { result: "paid" | "notpaid" | "unknown", version, userInfo } — what Swish CLAIMS
 *   { result: "missing" }    — returned without a result (no parameter)
 *   { result: "invalid" }    — returned with something that is not a documented result
 * Accepts protocol versions 1 and 2 (v2 adds userInfo { title, message }). Every return also
 * carries `instructionFingerprint`: which instruction was opened (null if the URL lacks it).
 */
export function readSwishReturn(search = window.location.search) {
  // The guide says Swish "appends" the result to the callback URL, not whether it joins with
  // "&" or "?" when that URL already has a query. Read both. (The result is URL-encoded JSON,
  // so it holds no bare "?" of its own.)
  const params = new URLSearchParams(String(search).replace(/^\?/, "").replace(/\?/g, "&"));
  if (params.get(SWISH_RETURN_PARAM) !== SWISH_RETURN_VALUE) return null;

  const opened = params.get(SWISH_FOR_PARAM);
  const instructionFingerprint = opened && FINGERPRINT.test(opened) ? opened : null;

  const raw = params.get(SWISH_RESULT_PARAM);
  if (raw == null || raw === "") return { result: "missing", instructionFingerprint };

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_) {
    return { result: "invalid", instructionFingerprint };
  }
  if (!parsed || typeof parsed !== "object") return { result: "invalid", instructionFingerprint };

  const version = Number(parsed.version);
  const result = typeof parsed.result === "string" ? parsed.result.toLowerCase() : null;
  if (![1, 2].includes(version) || !RESULTS.includes(result)) return { result: "invalid", instructionFingerprint };

  const userInfo =
    version === 2 && parsed.userInfo && typeof parsed.userInfo.title === "string" && typeof parsed.userInfo.message === "string"
      ? { title: parsed.userInfo.title, message: parsed.userInfo.message }
      : null;
  return { result, version, userInfo, instructionFingerprint };
}

/** Removes the Swish return marker from the address bar, so a reload never re-reads it. */
export function clearSwishReturn() {
  try {
    const url = new URL(window.location.href);
    // Also covers "?swish=return?swishResult=…": the whole tail is then the first value.
    url.searchParams.delete(SWISH_RETURN_PARAM);
    url.searchParams.delete(SWISH_FOR_PARAM);
    url.searchParams.delete(SWISH_RESULT_PARAM);
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  } catch (_) {
    /* best-effort */
  }
}

/**
 * Hands the payment to the Swish app. A mobile browser cannot tell whether Swish is installed
 * (guide §3.1.3): if the page is still in front a moment later, `onNotOpened` runs so the page
 * can explain. Must be called from the user's own tap (guide §4.1).
 */
export function openSwishApp(url, { onNotOpened, waitMs = 2500 } = {}) {
  let left = false;
  const markLeft = () => { left = true; };
  const onVisibility = () => { if (document.visibilityState === "hidden") left = true; };
  window.addEventListener("pagehide", markLeft);
  window.addEventListener("blur", markLeft);
  document.addEventListener("visibilitychange", onVisibility);

  const timer = window.setTimeout(() => {
    window.removeEventListener("pagehide", markLeft);
    window.removeEventListener("blur", markLeft);
    document.removeEventListener("visibilitychange", onVisibility);
    if (!left && document.visibilityState === "visible") onNotOpened?.();
  }, waitMs);

  window.location.href = url;
  return () => window.clearTimeout(timer);
}

/**
 * Where Swish can be reached from THIS screen (the Lovable payment design):
 *   "app"        a phone (or a phone on its side): Swish opens on the device;
 *   "qr-and-app" a touch screen too wide to be a phone (a tablet): scan, or open here;
 *   "qr"         a computer: the phone scans the code.
 * iPadOS reports itself as a Mac, so a Mac with touch counts as a tablet.
 */
export const WIDE_QUERY = "(min-width: 768px)";
export const LANDSCAPE_PHONE_QUERY = "(pointer: coarse) and (max-height: 540px)";
export const TOUCH_QUERY = "(pointer: coarse)";

export function readSwishReach() {
  if (typeof window === "undefined" || !window.matchMedia) return "app";
  if (!window.matchMedia(WIDE_QUERY).matches) return "app";
  if (window.matchMedia(LANDSCAPE_PHONE_QUERY).matches) return "app";
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const iPadOs = /Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1;
  return window.matchMedia(TOUCH_QUERY).matches || iPadOs ? "qr-and-app" : "qr";
}

export function subscribeSwishReach(onChange) {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const lists = [WIDE_QUERY, LANDSCAPE_PHONE_QUERY, TOUCH_QUERY].map((q) => window.matchMedia(q));
  lists.forEach((l) => l.addEventListener?.("change", onChange));
  return () => lists.forEach((l) => l.removeEventListener?.("change", onChange));
}

/** "150 000 kr", "98 765,50 kr" — never rounds öre away (formatSEK does). */
export function formatPaymentAmount(amount, language = "sv") {
  if (typeof amount !== "number" || !Number.isFinite(amount)) return "";
  const hasOre = Math.round(amount * 100) % 100 !== 0;
  const digits = { minimumFractionDigits: hasOre ? 2 : 0, maximumFractionDigits: 2 };
  if (language?.startsWith("en")) {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: "SEK", currencyDisplay: "code", ...digits }).format(amount);
  }
  return `${amount.toLocaleString("sv-SE", digits)} kr`;
}
